// CSV export button + utility.
//
// Pass `rows` (array of plain-object row data) and `filename`. The button
// renders a plain anchor that triggers a client-side blob download — no
// backend, no library. Works with any ReportTable row shape.
//
// Column order follows the keys of the first row. Values are stringified
// defensively (null/undefined → empty, nested objects → JSON) and quoted
// per RFC 4180 so Excel + Google Sheets both parse cleanly.

import React, { useMemo } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';

export function CsvExportButton({ rows, filename, disabled }) {
  const href = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    return URL.createObjectURL(blob);
  }, [rows]);

  const isDisabled = disabled || !href;

  return (
    <Tooltip label={isDisabled ? 'No data to export' : `Download ${rows?.length ?? 0} row${rows?.length === 1 ? '' : 's'} as CSV`}>
      <ActionIcon
        component="a"
        href={href ?? '#'}
        download={filename}
        size="sm"
        variant="subtle"
        color="gray"
        disabled={isDisabled}
        onClick={(e) => { if (isDisabled) e.preventDefault(); }}
      >
        <IconDownload size={14} />
      </ActionIcon>
    </Tooltip>
  );
}

export function toCsv(rows) {
  if (!rows || rows.length === 0) return '';
  const keys = Object.keys(rows[0]);
  const header = keys.map(csvEscape).join(',');
  const body = rows.map((r) => keys.map((k) => csvEscape(r[k])).join(',')).join('\r\n');
  return `${header}\r\n${body}\r\n`;
}

function csvEscape(v) {
  if (v == null) return '';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  // RFC 4180: wrap in quotes if contains quote, comma, newline; escape inner quotes.
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
